function path(root, sublink) {
  return `${root}${sublink}`;
}

const ROOTS_AUTH = '/auth';
const ROOTS_DASHBOARD = '/admin';
const ROOTS_APPS = '/apps';
const ROOTS_GIFTCARD = '/giftcard';

export const PATH_AUTH = {
  root: ROOTS_AUTH,
  login: path(ROOTS_AUTH, '/login'),
  loginUnprotected: path(ROOTS_AUTH, '/login-unprotected'),
  register: path(ROOTS_AUTH, '/register'),
  registerUnprotected: path(ROOTS_AUTH, '/register-unprotected'),
  resetPassword: path(ROOTS_AUTH, '/reset-password'),
  verify: path(ROOTS_AUTH, '/verify'),
};

export const PATH_PAGE = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  page404: '/404',
  page500: '/500',
  unauthorized: '/unauthorized',
};

export const PATH_APP = {
  root: ROOTS_APPS,
  selector: ROOTS_APPS,
};

export const PATH_GIFTCARD = {
  root: ROOTS_GIFTCARD,
  orders: path(ROOTS_GIFTCARD, '/orders'),
};

const dashboard = path(ROOTS_DASHBOARD, '/dashboard');
const accessControl = path(ROOTS_DASHBOARD, '/access');
const riskPolicy = path(ROOTS_DASHBOARD, '/risk');
const users = path(ROOTS_DASHBOARD, '/users');
const transactions = path(ROOTS_DASHBOARD, '/transactions');
const settlementOps = path(ROOTS_DASHBOARD, '/settlement');
const queueJobs = path(ROOTS_DASHBOARD, '/queues');
const serviceReports = path(ROOTS_DASHBOARD, '/services');
const blockchainOps = path(ROOTS_DASHBOARD, '/blockchain');
const sos = path(ROOTS_DASHBOARD, '/sos');
const adjustments = path(ROOTS_DASHBOARD, '/adjustments');
const config = path(ROOTS_DASHBOARD, '/config');
const legacy = path(ROOTS_DASHBOARD, '/legacy');

export const PATH_DASHBOARD = {
  root: ROOTS_DASHBOARD,
  dashboard: {
    root: dashboard,
  },
  accessControl: {
    root: accessControl,
    staff: path(accessControl, '/staff'),
    requests: path(accessControl, '/requests'),
    grants: path(accessControl, '/grants'),
    audit: path(accessControl, '/audit'),
  },
  riskPolicy: {
    root: riskPolicy,
    summary: path(riskPolicy, '/summary'),
    decisionLogs: path(riskPolicy, '/decision-logs'),
    thresholds: path(riskPolicy, '/thresholds'),
    simulator: path(riskPolicy, '/simulator'),
  },
  users: {
    root: users,
    allUsers: path(users, '/all-users'),
    details: (userId) => path(users, `/all-users/${userId}`),
    walletBalances: path(users, '/wallet-balances'),
    controls: path(users, '/controls'),
    primaryAccount: path(users, '/primary-account'),
    policyDecisions: path(users, '/policy-decisions'),
    beneficiaries: path(users, '/beneficiaries'),
  },
  transactions: {
    root: transactions,
    history: path(transactions, '/history'),
    blockchain: path(transactions, '/blockchain'),
    instantSwaps: path(transactions, '/instant-swaps'),
    details: (transactionId) => path(transactions, `/${transactionId}`),
  },
  settlementOps: {
    root: settlementOps,
    overview: path(settlementOps, '/overview'),
    queue: path(settlementOps, '/queue'),
    failed: path(settlementOps, '/failed'),
    stalled: path(settlementOps, '/stalled'),
    recoveryLog: path(settlementOps, '/recovery-log'),
  },
  queueJobs: {
    root: queueJobs,
    overview: path(queueJobs, '/overview'),
    queued: path(queueJobs, '/queued'),
    reserved: path(queueJobs, '/reserved'),
    failed: path(queueJobs, '/failed'),
    outbox: path(queueJobs, '/outbox'),
  },
  serviceReports: {
    root: serviceReports,
    overview: path(serviceReports, '/overview'),
    incidents: path(serviceReports, '/incidents'),
    blockchain: path(serviceReports, '/blockchain'),
    provider: (provider) => path(serviceReports, `/provider/${provider}`),
  },
  blockchainOps: {
    root: blockchainOps,
    blockchainTx: path(blockchainOps, '/timeline/blockchain'),
    quote: path(blockchainOps, '/timeline/quote'),
    transaction: path(blockchainOps, '/timeline/transaction'),
  },
  sos: {
    root: sos,
    overview: path(sos, '/overview'),
    signals: path(sos, '/signals'),
  },
  adjustments: {
    root: adjustments,
    list: path(adjustments, '/list'),
    create: path(adjustments, '/create'),
    details: (requestId) => path(adjustments, `/${requestId}`),
  },
  config: {
    root: config,
    groups: path(config, '/groups'),
    group: (groupKey) => path(config, `/groups/${groupKey}`),
    featureFlags: path(config, '/feature-flags'),
    audit: path(config, '/audit'),
  },
  legacy: {
    root: legacy,
    adminManagement: {
      root: path(legacy, '/admin-management'),
      list: path(legacy, '/admin-management/list'),
    },
    cryptoAssets: {
      root: path(legacy, '/crypto-assets'),
      list: path(legacy, '/crypto-assets/list'),
      ranges: path(legacy, '/crypto-assets/ranges'),
    },
    content: {
      root: path(legacy, '/content'),
      featuredPosts: path(legacy, '/content/featured-posts'),
      appPosts: path(legacy, '/content/app-posts'),
    },
    cruiseCampaigns: {
      root: path(legacy, '/cruise-campaigns'),
      cruiseOverview: path(legacy, '/cruise-campaigns/cruise-overview'),
      cruiseManage: path(legacy, '/cruise-campaigns/cruise-manage'),
      cruiseBatch: path(legacy, '/cruise-campaigns/cruise-batch'),
      cruiseHistory: path(legacy, '/cruise-campaigns/cruise-history'),
      cruiseTelemetry: path(legacy, '/cruise-campaigns/cruise-telemetry'),
      campaigns: path(legacy, '/cruise-campaigns/campaigns'),
      campaignDetails: (campaignId) => path(legacy, `/cruise-campaigns/campaigns/${campaignId}/details`),
    },
    giftcardOperations: {
      root: path(legacy, '/giftcard-operations'),
      giftcardOrders: path(legacy, '/giftcard-operations/orders'),
    },
    compliance: {
      root: path(legacy, '/compliance'),
      queue: path(legacy, '/compliance/queue'),
      overview: path(legacy, '/compliance/overview'),
      health: path(legacy, '/compliance/health'),
      history: path(legacy, '/compliance/history'),
    },
    userManagement: {
      root: path(legacy, '/user-management'),
      list: path(legacy, '/user-management/list'),
      wallet: path(legacy, '/user-management/wallet'),
    },
    systemReports: {
      root: path(legacy, '/system-reports'),
      variables: path(legacy, '/system-reports/variables'),
      referralRanking: path(legacy, '/system-reports/referral-ranking'),
      monthlyBonus: path(legacy, '/system-reports/monthly-bonus'),
      activities: path(legacy, '/system-reports/activities'),
    },
    blockchainViews: {
      root: path(legacy, '/blockchain-views'),
      quidaxAccounts: path(legacy, '/blockchain-views/quidax-accounts'),
      walletAddresses: path(legacy, '/blockchain-views/wallet-addresses'),
      instantSwaps: path(legacy, '/blockchain-views/instant-swaps'),
    },
  },

  // Legacy aliases kept so active legacy pages and older internal links can continue to resolve.
  general: {
    overview: dashboard,
    usersOverview: dashboard,
    transactionOverview: dashboard,
  },
  user: {
    root: path(legacy, '/user-management'),
    list: path(legacy, '/user-management/list'),
    wallet: path(legacy, '/user-management/wallet'),
    account: dashboard,
  },
  compliance: {
    root: path(legacy, '/compliance'),
    overview: path(legacy, '/compliance/overview'),
    queue: path(legacy, '/compliance/queue'),
    health: path(legacy, '/compliance/health'),
    inReview: path(legacy, '/compliance/queue'),
    history: path(legacy, '/compliance/history'),
    userReview: (userId) => path(legacy, `/compliance/review/${userId}`),
  },
  order: {
    root: path(legacy, '/giftcard-operations'),
    list: path(legacy, '/giftcard-operations/orders'),
    giftcard: path(legacy, '/giftcard-operations/orders'),
  },
  rewards: {
    root: path(legacy, '/system-reports'),
    referralRanking: path(legacy, '/system-reports/referral-ranking'),
    monthlyBonus: path(legacy, '/system-reports/monthly-bonus'),
  },
  trading: {
    root: path(legacy, '/crypto-assets'),
    list: path(legacy, '/crypto-assets/list'),
    ranges: path(legacy, '/crypto-assets/ranges'),
  },
  content: {
    root: path(legacy, '/content'),
    featuredPosts: path(legacy, '/content/featured-posts'),
    managePosts: path(legacy, '/content/app-posts'),
  },
  cruiseCenter: {
    root: path(legacy, '/cruise-campaigns'),
    overview: path(legacy, '/cruise-campaigns/cruise-overview'),
    manage: path(legacy, '/cruise-campaigns/cruise-manage'),
    history: path(legacy, '/cruise-campaigns/cruise-history'),
    batch: path(legacy, '/cruise-campaigns/cruise-batch'),
    telemetry: path(legacy, '/cruise-campaigns/cruise-telemetry'),
  },
  pushNotification: {
    root: path(legacy, '/cruise-campaigns'),
    campaign: path(legacy, '/cruise-campaigns/campaigns'),
    campaignDetails: path(legacy, '/cruise-campaigns/campaign/:id/details'),
  },
  activityLog: path(legacy, '/system-reports/activities'),
  system: {
    adminAccount: path(legacy, '/admin-management/list'),
    supportAgent: path(ROOTS_DASHBOARD, '/unauthorized-support-agents'),
    configurations: {
      root: path(legacy, '/system-reports'),
      variables: path(legacy, '/system-reports/variables'),
    },
  },
};
